/**
 * World / canon enums — single source of truth.
 *
 * Each NPC / location / faction / journal / chronicle / relationship enum used
 * to be declared three to four times: a const tuple + a hand-written
 * `t.Union([...])` + a backend `narrow*()` switch, plus a literal-union alias on
 * the frontend. They now live here once, mirroring the `MEETING_STATUSES`
 * pattern in `meeting-status.ts`.
 *
 * - Backend `model.ts` derives its Elysia schema with
 *   `t.Union(TUPLE.map((v) => t.Literal(v)))` and re-exports the `T` type under
 *   any module-local alias it still needs.
 * - Backend `index.ts` narrows DB strings with the generic
 *   `narrow(value, TUPLE, FALLBACK)` helper instead of a per-enum switch.
 * - Frontend imports the `T` types directly instead of redeclaring literal
 *   unions.
 *
 * Renames then become a single-file edit, caught at compile time on every
 * consumer.
 */

// ---------------------------------------------------------------------------
// NPCs
// ---------------------------------------------------------------------------

export const NPC_STATUSES = ["alive", "dead", "unknown", "fled", "transformed"] as const;
export type TNpcStatus = (typeof NPC_STATUSES)[number];

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

export const LOCATION_TYPES = ["city", "region", "dungeon", "wilderness", "structure", "plane", "realm"] as const;
export type TLocationType = (typeof LOCATION_TYPES)[number];

export const LOCATION_STATUSES = ["known", "visited", "destroyed", "lost", "unreachable"] as const;
export type TLocationStatus = (typeof LOCATION_STATUSES)[number];

// ---------------------------------------------------------------------------
// Factions
// ---------------------------------------------------------------------------

export const FACTION_TYPES = ["guild", "house", "cult", "government", "order", "company", "sect"] as const;
export type TFactionType = (typeof FACTION_TYPES)[number];

export const FACTION_STANDINGS = ["allied", "friendly", "neutral", "hostile", "enemy", "unknown"] as const;
export type TFactionStanding = (typeof FACTION_STANDINGS)[number];

// ---------------------------------------------------------------------------
// Journal + Chronicle entry types
// ---------------------------------------------------------------------------

export const JOURNAL_ENTRY_TYPES = [
	"achievement",
	"milestone",
	"knowledge",
	"relationship",
	"history",
	"quote",
	"wound",
	"item",
	"goal",
] as const;
export type TJournalEntryType = (typeof JOURNAL_ENTRY_TYPES)[number];

export const CHRONICLE_ENTRY_TYPES = [
	"event",
	"discovery",
	"decision",
	"relationship_change",
	"world_change",
	"introduction",
] as const;
export type TChronicleEntryType = (typeof CHRONICLE_ENTRY_TYPES)[number];

// ---------------------------------------------------------------------------
// Canon lifecycle — journal & chronicle share an identical tri-state status and
// provenance, so they collapse to one tuple each.
// ---------------------------------------------------------------------------

export const CANON_STATUSES = ["pending", "accepted", "rejected"] as const;
export type TCanonStatus = (typeof CANON_STATUSES)[number];

export const CANON_PROPOSED_BY = ["live", "post", "user"] as const;
export type TCanonProposedBy = (typeof CANON_PROPOSED_BY)[number];

// ---------------------------------------------------------------------------
// World relationships — kind-tag on source/target edges.
// ---------------------------------------------------------------------------

export const WORLD_RELATIONSHIP_KINDS = ["npc", "faction"] as const;
export type TWorldRelationshipKind = (typeof WORLD_RELATIONSHIP_KINDS)[number];
